import {
  TellescopeModelFromName,
  usePermissionQuery,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
} from "@tellescope/tanstack-query";
import { CalendarFilters, DEFAULT_CALENDAR_FILTERS, FAKE_CALENDAR_USER_ID } from "@tellescope/calendar";
import { deepEqual } from "@tellescope/utils";
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { TELLESCOPE_2_TABLE_VIEW_PREFIX } from "./useTableView";
import { useShallowCalendarStore } from "../store";

export const CALENDAR_TABLE_VIEW_PAGE = `${TELLESCOPE_2_TABLE_VIEW_PREFIX}Calendar` as const;
export const ALL_CALENDAR_VIEW_ID = "all" as const;

export interface UseCalendarTableViewsResult {
  views: TellescopeModelFromName<"table_views">[];
  selectedView: TellescopeModelFromName<"table_views"> | undefined;
  viewId: string;
  setViewId: (id: string) => void;
  isAllView: boolean;
  isLoadingViews: boolean;
  stagedFilters: CalendarFilters;
  setStagedFilters: (filters: CalendarFilters) => void;
  applyFilters: (filters: CalendarFilters) => void;
  resetStagedFilters: () => void;
  createView: (title: string, filters: CalendarFilters) => Promise<string>;
  deleteSelectedView: () => Promise<void>;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  effectiveFilters: CalendarFilters;
  hasUnsavedChanges: boolean;
}

const parseFiltersFromView = (view: TellescopeModelFromName<"table_views"> | undefined): CalendarFilters => {
  if (!view?.filter) return DEFAULT_CALENDAR_FILTERS;
  return view.filter as CalendarFilters;
};

export const useCalendarTableViews = (): UseCalendarTableViewsResult => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const viewId = params.viewId || ALL_CALENDAR_VIEW_ID;

  const setViewId = useCallback(
    (id: string) => {
      const nextViewId = id || ALL_CALENDAR_VIEW_ID;
      const segments = location.pathname.split("/");
      if (segments[1] !== "calendar" || segments[2] !== "view") return;
      segments[3] = nextViewId;
      navigate(segments.join("/"), { replace: true });
    },
    [location.pathname, navigate]
  );

  const viewsQuery = useTellescopeInfiniteQuery({
    model: "table_views",
    method: "getSome",
    request: {
      limit: 500,
      filter: { page: CALENDAR_TABLE_VIEW_PAGE },
    },
    select: (data) => data.pages.flat(),
  });

  const {
    create: { access: canCreate },
    update: { access: canUpdate },
    delete: { access: canDelete },
  } = usePermissionQuery({
    model: "table_views",
    actions: ["create", "update", "delete"],
    recordId: viewId !== "all" ? viewId : undefined,
  });

  const createMutation = useTellescopeMutation({ model: "table_views", method: "createOne" });
  const updateMutation = useTellescopeMutation({ model: "table_views", method: "updateOne" });
  const deleteMutation = useTellescopeMutation({ model: "table_views", method: "deleteOne" });

  const views = useMemo(() => viewsQuery.data ?? [], [viewsQuery.data]);
  const selectedView = useMemo(() => views.find((view) => view.id === viewId), [views, viewId]);
  const isAllView = viewId === ALL_CALENDAR_VIEW_ID || !selectedView;

  const persistedFilters = useMemo(() => parseFiltersFromView(selectedView), [selectedView]);

  const [localFiltersByViewId, setLocalFiltersByViewId] = useState<Record<string, CalendarFilters>>({});
  const [trackedViewId, setTrackedViewId] = useState(viewId);
  if (trackedViewId !== viewId) {
    setTrackedViewId(viewId);
  }
  const { selectedUserIds } = useShallowCalendarStore("selectedUserIds");

  const localFilters = localFiltersByViewId[viewId];
  const initialFilters = localFilters ?? (isAllView ? DEFAULT_CALENDAR_FILTERS : persistedFilters);
  const effectiveFilters = useMemo(() => {
    const result = initialFilters;
    const allUserIds = Array.from(new Set([...(result.userIds ?? []), ...selectedUserIds]));
    const calculatedUserIds = allUserIds.length
      ? allUserIds.filter(
          (id) =>
            (initialFilters.userIds?.length ? initialFilters.userIds.includes(id) : true) &&
            (selectedUserIds.length ? selectedUserIds.includes(id) : true)
        )
      : initialFilters.userIds;
    const userIds = allUserIds.length && !calculatedUserIds?.length ? [FAKE_CALENDAR_USER_ID] : calculatedUserIds;

    return {
      ...initialFilters,
      userIds,
    };
  }, [initialFilters, selectedUserIds]);

  const setStagedFilters = useCallback(
    (filters: CalendarFilters) => {
      setLocalFiltersByViewId((prev) => ({ ...prev, [viewId]: filters }));
    },
    [viewId]
  );

  const resetStagedFilters = useCallback(() => {
    setLocalFiltersByViewId((prev) => {
      if (!(viewId in prev)) return prev;
      const next = { ...prev };
      delete next[viewId];
      return next;
    });
  }, [viewId]);

  const applyFilters = useCallback(
    (filters: CalendarFilters) => {
      if (isAllView) {
        setStagedFilters(filters);
        return;
      }
      if (canUpdate && selectedView) {
        updateMutation.mutate(
          {
            id: selectedView.id,
            filter: filters,
            _options: { replaceObjectFields: true },
          },
          {
            onSuccess: () => {
              setLocalFiltersByViewId((prev) => {
                if (!(viewId in prev)) return prev;
                const { [viewId]: _, ...next } = prev;
                return next;
              });
            },
          }
        );
        return;
      }
      setStagedFilters(filters);
    },
    [canUpdate, isAllView, selectedView, setStagedFilters, updateMutation, viewId]
  );

  const createView = useCallback(
    async (title: string, filters: CalendarFilters): Promise<string> => {
      const created = await createMutation.mutateAsync({
        page: CALENDAR_TABLE_VIEW_PAGE,
        title,
        columns: [],
        filter: filters,
      });
      resetStagedFilters();
      return created.id;
    },
    [createMutation, resetStagedFilters]
  );

  const deleteSelectedView = useCallback(async () => {
    if (!selectedView) return;
    await deleteMutation.mutateAsync(selectedView.id);
    setViewId(ALL_CALENDAR_VIEW_ID);
  }, [deleteMutation, selectedView, setViewId]);

  const savedBaseline = isAllView ? DEFAULT_CALENDAR_FILTERS : persistedFilters;
  const hasUnsavedChanges =
    localFilters !== undefined && !deepEqual(localFilters, savedBaseline, { includeRemovals: true });

  return {
    views,
    selectedView,
    viewId,
    setViewId,
    isAllView,
    isLoadingViews: viewsQuery.isLoading,
    stagedFilters: effectiveFilters,
    setStagedFilters,
    applyFilters,
    resetStagedFilters,
    createView,
    deleteSelectedView,
    canCreate,
    canUpdate,
    canDelete,
    effectiveFilters,
    hasUnsavedChanges,
  };
};
